import { Box, Stack } from "@mui/material";
import {
  AcquisitionMiniCard,
  AcquisitionSummaryCard,
} from "./AcquisitionCards";

interface TargetVsActualItem {
  Proportional_Target: number;
  Actual: number;
  Target: number;
  Comment: string;
  Metrics: string;
  RR: number;
  Proportional_Attainment: number;
}

interface AcquisitionProps {
  data: TargetVsActualItem[];
}

const IS_CURRENCY: Record<string, boolean> = {
  spend: true,
  clicks: false,
  cvr: false,
};

const IS_PERCENTAGE: Record<string, boolean> = {
  spend: false,
  clicks: false,
  cvr: true,
};

function formatValue(value: number, metricKey: string): string {
  // CVR values are stored as decimals (e.g., 0.1372) — multiply by 100 for display
  if (IS_PERCENTAGE[metricKey]) return `${(value * 100).toFixed(1)}%`;
  const rounded = Math.round(value);
  const formatted = rounded.toLocaleString();
  return IS_CURRENCY[metricKey] ? `$${formatted}` : formatted;
}

// Spend - Red → > 110% (inverted: lower spend is better)
function getSpendRrColor(attainment: number): "success" | "warning" | "error" {
  if (attainment > 1.1) return "error"; // Red → > 110%
  return "success";
}

// Clicks / Conversions - positive metrics
// Red → below 85%, Yellow → 85-99%, Green → > 99%
function getPositiveRrColor(
  attainment: number,
): "success" | "warning" | "error" {
  if (attainment > 0.99) return "success"; // Green → > 99%
  if (attainment >= 0.85) return "warning"; // Yellow → 85% to 99%
  return "error"; // Red → < 85%
}

export default function Acquisition({ data }: AcquisitionProps) {
  const findMetric = (name: string) =>
    data?.find((item) => item.Metrics?.toLowerCase() === name);

  const spend = findMetric("spend");
  const clicks = findMetric("clicks");
  const conversions = findMetric("cvr");

  return (
    <Box>
      <Stack spacing={2}>
        {spend && (
          <AcquisitionSummaryCard
            title="Spend"
            value={formatValue(spend.Actual, "spend")}
            total={formatValue(spend.Target, "spend")}
            rr={`${(spend.Proportional_Attainment * 100).toFixed(1)}%`}
            prr={formatValue(spend.RR, "spend")}
            rrColor={getSpendRrColor(spend.Proportional_Attainment)}
          />
        )}

        <Stack direction="row" spacing={2}>
          {clicks && (
            <AcquisitionMiniCard
              title="CLICKS"
              value={formatValue(clicks.Actual, "clicks")}
              total={formatValue(clicks.Target, "clicks")}
              rr={`${(clicks.Proportional_Attainment * 100).toFixed(1)}%`}
              pr={formatValue(clicks.RR, "clicks")}
              rrColor={getPositiveRrColor(clicks.Proportional_Attainment)}
            />
          )}

          {conversions && (
            <AcquisitionMiniCard
              title="CONVERSIONS"
              value={formatValue(conversions.Actual, "cvr")}
              total={formatValue(conversions.Target, "cvr")}
              rr={`${(conversions.Proportional_Attainment * 100).toFixed(1)}%`}
              pr={formatValue(conversions.RR, "cvr")}
              rrColor={getPositiveRrColor(conversions.Proportional_Attainment)}
            />
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
